import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	CreateReportInputSchema,
	CreateReportScheduleInputSchema,
	LightningFilteredReportDataUrlSchema,
	type LightningGetFilteredReportDataUrlInput,
	type LightningReportContinuingCurrentProbability,
	LightningReportDatasetSchema,
	LightningReportFaultTimeSchema,
	LightningReportScheduleBaseSchema,
	type LightningReportScheduleStatus,
	LightningReportSchema,
	LightningReportTimeRangeSchema,
	lightningReportScheduleDaysOfWeek,
	lightningReportScheduleFrequencies,
	OneOffLightningReportScheduleFieldsSchema,
	RepeatedLightningReportScheduleFieldsSchema,
} from '@xweather/protect-types'
import type { Feature, GeometryCollection, Point } from 'geojson'
import { gql } from 'graphql-request'
import { z } from 'zod'
import { createGraphQLClient, stripNulls } from '../../api/helpers.ts'
import { dayjs } from '../../dayjs.ts'
import { env } from '../../env.ts'
import { useFlags } from '../../launchdarkly.ts'
import { toAnalyzeV2Request, toAssetVrns } from '../analyze/assetVrns.ts'
import {
	createAnalyzeReport,
	createReportDownloadUrl,
	getAnalyzeReport,
	getAnalyzeReportData,
	listAnalyzeReports,
} from '../analyze/reports.ts'
import { getLightningDataAvailability } from '../analyze/reportTypeRules.ts'
import { createAnalyzeReportSchedule, listAnalyzeReportSchedules, updateAnalyzeReportSchedule } from '../analyze/schedules.ts'
import { type Impersonation, useImpersonation } from '../impersonation.ts'

const useAnalyzeV2 = () => !!useFlags().analyzeV2LightningReports

type LightningReportQueryProps = {
	includeGeometry: boolean
}
const lightningReport = ({ includeGeometry }: LightningReportQueryProps) => gql`
	{
		id
		productVrn
		scheduleId
		assetVrn
		${includeGeometry ? 'geometry { type coordinates }' : ''}
		geometryBbox
		status
		name
		radiusKm
		preferences {
			unit
			timezone
			dateFormat
		}
		timeRange {
			start
			end
		}
		faultTime { time buffer { unit amount } }
		events {
			cloudToCloud
			positiveCloudToGround
			negativeCloudToGround
			minSignalStrengthKA
			maxSignalStrengthKA
		}
		errorEllipseConfidence
		fields
		dataset
		signalStrengthRanges {
			min
			max
		}
		includeCloudToCloud
		customPoint { type coordinates }
		createdAt
	}
`

const createLightningReport = () => gql`
	mutation CreateLightningReport($input: CreateReportInput!) {
		createReport(input: $input) {
			id
			timeRange {
				start
				end
			}
		}
	}
`

const createLightningReportSchedule = () => gql`
	mutation CreateLightningReportSchedule($input: CreateReportScheduleInput!) {
		createReportSchedule(input: $input) {
			id
		}
	}
`

const getLightningReport = (props: LightningReportQueryProps) => gql`
	query GetReport($id: ID!) {
    	getReport (id: $id) ${lightningReport(props)}
	}
`

const listLightningReports = (props: LightningReportQueryProps) => gql`
	query ListReports($nextToken: String) {
    	listReportsV2(nextToken: $nextToken) {
			nextToken
			items ${lightningReport(props)}
		}
	}
`

const lightningReportSchedule = () => gql`
	{
		id
		productVrn
		assetVrn
		geometryBbox
		status
		radiusKm
		name
		preferences { unit timezone dateFormat }
		createdAt
		cancelledAt
		nextScheduledReportAt
		errorEllipseConfidence
		includeCloudToCloud
		signalStrengthRanges { min max }
		customPoint { type coordinates }
		dataset
		fields
		scheduleType
		frequency
		dayOfWeek
		startScheduleAt
		stopScheduleAt
		timeRange { start end }
		faultTime { time buffer { unit amount } }
	}
`

const listLightningReportSchedules = () => gql`
	query ListReportSchedules($nextToken: String) {
		listReportSchedules(nextToken: $nextToken) {
			nextToken
			items ${lightningReportSchedule()}
		}
	}
`

const updateLightningReportSchedule = () => gql`
	mutation UpdateReportSchedule($input: UpdateReportScheduleInput!) {
		updateReportSchedule(input: $input) {
			id
		}
	}
`

const getLightningReportDataUrlsGQL = () => gql`
	query GetReportDataUrls($id: ID!) {
		getReportDataUrls(id: $id) {
			geojson
		}
	}
`

const getLightningReportDataAvailability = () => gql`
	query GetReportDataAvailability($input: GetReportDataAvailabilityInput!) {
		getReportDataAvailability(input: $input) {
			start
			end
		}
	}
`

const getFilteredReportDataUrl = gql`
	query GetFilteredReportDataUrl($input: GetFilteredReportDataUrlInput!) {
		getFilteredReportDataUrl(input: $input) {
			url
		}
	}
`

const getClient = (impersonation: Impersonation | undefined) => createGraphQLClient(env.API_LIGHTNING_REPORTS, impersonation)

export const useCreateLightningReport = () => {
	const impersonation = useImpersonation()
	const analyzeV2 = useAnalyzeV2()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: CreateLightningReportRequest) =>
			analyzeV2
				? createAnalyzeReport({ ...input, reportType: 'LIGHTNING' }, impersonation)
				: getClient(impersonation).request(createLightningReport(), {
						input: toAnalyzeV1Request(CreateLightningReportRequestSchema.decode(input)),
					}),
		onSuccess: async () => queryClient.invalidateQueries(lightningReportsQuery(impersonation, analyzeV2)),
		onError: (error) => console.error('Error creating lightning report:', error),
	})
}

export const useLightningReportDataAvailability = (input: GetLightningReportDataAvailabilityRequest) => {
	const impersonation = useImpersonation()
	const analyzeV2 = useAnalyzeV2()
	return useQuery({
		queryFn: async () =>
			analyzeV2
				? // Null for a dataset the subscription may not use
					((await getLightningDataAvailability(impersonation)).coverageZones[input.dataset] ?? null)
				: GetLightningReportDataAvailabilityResponseSchema.parse(
						await getClient(impersonation).request(getLightningReportDataAvailability(), {
							input: GetLightningReportDataAvailabilityRequestSchema.decode(input),
						}),
					).getReportDataAvailability,
		queryKey: ['LIGHTNING_REPORT_DATA_AVAILABILITY', input.dataset, impersonation?.subscriptionId, analyzeV2],
		refetchInterval: dayjs.duration(10, 'seconds').asMilliseconds(),
	})
}

// Analyze v1 takes the one asset of a report as `assetVrn`.
const toAnalyzeV1Request = <T extends { assetVrns: string[] }>({ assetVrns, ...request }: T) => {
	const [assetVrn] = assetVrns
	if (assetVrn === undefined || assetVrns.length > 1) throw new Error('Analyze v1 takes exactly one asset')
	return { ...request, assetVrn }
}

const GetLightningReportDataAvailabilityRequestSchema = z.object({
	dataset: LightningReportDatasetSchema,
})
const GetLightningReportDataAvailabilityResponseSchema = stripNulls(z.object({ getReportDataAvailability: LightningReportTimeRangeSchema }))
type GetLightningReportDataAvailabilityRequest = z.infer<typeof GetLightningReportDataAvailabilityRequestSchema>

const AssetVrnsSchema = CreateReportInputSchema.options[0].shape.assetVrns

// Shared by both CreateLightningReport and CreateLightningReportSchedule.
export const CreateLightningReportOrScheduleRequestCommonFieldsSchema = z.object({
	...LightningReportSchema.pick({
		name: true,
		radiusKm: true,
		geometry: true,
		customPoint: true,
		preferences: true,
		errorEllipseConfidence: true,
		includeCloudToCloud: true,
		signalStrengthRanges: true,
		dataset: true,
	}).shape,
	assetVrns: AssetVrnsSchema,
	includeStrikeDamagePotential: z.boolean(),
})

export const CreateRepeatedLightningReportScheduleRequestSchema = CreateLightningReportOrScheduleRequestCommonFieldsSchema.extend({
	scheduleType: z.literal('REPEATED'),
	frequency: z.enum(lightningReportScheduleFrequencies),
	dayOfWeek: z.enum(lightningReportScheduleDaysOfWeek).optional(),
	startScheduleAt: z.iso.datetime(),
	stopScheduleAt: z.iso.datetime().optional(),
})

export const CreateOneOffLightningReportScheduleRequestSchema = CreateLightningReportOrScheduleRequestCommonFieldsSchema.extend({
	scheduleType: z.literal('ONE_OFF'),
	timeRange: LightningReportTimeRangeSchema,
	faultTime: LightningReportFaultTimeSchema.optional(),
})

export const CreateLightningReportScheduleRequestSchema = z.union([
	CreateRepeatedLightningReportScheduleRequestSchema,
	CreateOneOffLightningReportScheduleRequestSchema,
])
export type CreateLightningReportScheduleRequest = z.infer<typeof CreateLightningReportScheduleRequestSchema>

export const useCreateLightningReportSchedule = () => {
	const impersonation = useImpersonation()
	const analyzeV2 = useAnalyzeV2()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: CreateLightningReportScheduleRequest) => {
			if (analyzeV2) {
				// The request lets any repeated schedule leave out its day of the week, which analyze-v2 requires of a weekly one
				return createAnalyzeReportSchedule(
					toAnalyzeV2Request(CreateReportScheduleInputSchema, { ...input, reportType: 'LIGHTNING' }),
					impersonation,
				)
			}

			const finalInput = toAnalyzeV1Request(CreateLightningReportScheduleRequestSchema.parse(input))
			return getClient(impersonation).request(createLightningReportSchedule(), { input: finalInput })
		},
		onSuccess: () => queryClient.invalidateQueries(lightningReportSchedulesQuery(impersonation, { enabled: true, analyzeV2 })),
		onError: (error) => console.error('Error creating lightning report schedule:', error),
	})
}

export const CreateLightningReportRequestSchema = CreateLightningReportOrScheduleRequestCommonFieldsSchema.extend({
	timeRange: LightningReportTimeRangeSchema,
	faultTime: LightningReportFaultTimeSchema.optional(),
})
export type CreateLightningReportRequest = z.infer<typeof CreateLightningReportRequestSchema>

export const lightningReportQuery = (id: string, impersonation: Impersonation | undefined, analyzeV2: boolean) =>
	queryOptions({
		queryKey: ['LIGHTNING_REPORT', id, impersonation?.subscriptionId, analyzeV2],
		refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 1000 * 15 : Infinity),
		staleTime: 1000 * 60,
		queryFn: async () => {
			// analyze-v2 answers with the report itself, which v1 wraps in `getReport`
			const response = analyzeV2
				? { getReport: await getAnalyzeReport(id, impersonation) }
				: await getClient(impersonation).request(getLightningReport({ includeGeometry: true }), { id })
			const parseResult = GetLightningReportSchema.safeParse(response)
			if (parseResult.success) {
				return parseResult.data.getReport
			} else {
				console.error('Error parsing lightning report data', parseResult.error)
				throw parseResult.error
			}
		},
	})

export const lightningReportsQuery = (impersonation: Impersonation | undefined, analyzeV2: boolean) =>
	queryOptions({
		queryKey: ['LIGHTNING_REPORTS', impersonation?.subscriptionId, analyzeV2],
		refetchInterval: (query) => 1000 * (query.state.data?.some((report) => report.status === 'PENDING') ? 15 : 60),
		staleTime: 1000 * 60,
		queryFn: async () => {
			if (analyzeV2) return z.array(ListedLightningReportSchema).parse(await listAnalyzeReports('LIGHTNING', impersonation))

			let nextToken: string | undefined
			const reports: ListedLightningReport[] = []
			do {
				const response = await getClient(impersonation).request(listLightningReports({ includeGeometry: false }), {
					nextToken,
				})
				const parseResult = ListLightningReportsSchema.safeParse(response)
				if (parseResult.success) {
					const { nextToken: newNextToken, items } = parseResult.data.listReportsV2
					nextToken = newNextToken
					reports.push(...items)
				} else {
					console.error('Error parsing lightning report data', parseResult.error)
					throw parseResult.error
				}
			} while (nextToken !== undefined)
			return reports
		},
	})

export const useLightningReport = (id: string) => {
	const impersonation = useImpersonation()
	return useQuery(lightningReportQuery(id, impersonation, useAnalyzeV2()))
}

export const useLightningReports = () => {
	const impersonation = useImpersonation()
	return useQuery(lightningReportsQuery(impersonation, useAnalyzeV2()))
}

export const lightningReportSchedulesQuery = (
	impersonation: Impersonation | undefined,
	{ enabled, analyzeV2 }: { enabled?: boolean; analyzeV2: boolean },
) =>
	queryOptions({
		queryKey: ['LIGHTNING_REPORT_SCHEDULES', impersonation?.subscriptionId, analyzeV2],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60,
		enabled,
		queryFn: async () => {
			if (analyzeV2) return z.array(ListedLightningReportScheduleSchema).parse(await listAnalyzeReportSchedules('LIGHTNING', impersonation))

			let nextToken: string | undefined
			const schedules: ListedLightningReportSchedule[] = []
			do {
				const response = await getClient(impersonation).request(listLightningReportSchedules(), { nextToken })
				const parseResult = ListLightningReportSchedulesSchema.safeParse(response)
				if (parseResult.success) {
					const { nextToken: newNextToken, items } = parseResult.data.listReportSchedules
					nextToken = newNextToken
					schedules.push(...items)
				} else {
					console.error('Error parsing lightning report schedule data', parseResult.error)
					throw parseResult.error
				}
			} while (nextToken !== undefined)
			return schedules
		},
	})

export const useLightningReportSchedules = (opts: { enabled: boolean } = { enabled: true }) => {
	const impersonation = useImpersonation()
	return useQuery(lightningReportSchedulesQuery(impersonation, { ...opts, analyzeV2: useAnalyzeV2() }))
}

export const useUpdateLightningReportSchedule = () => {
	const impersonation = useImpersonation()
	const analyzeV2 = useAnalyzeV2()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (input: { id: string; status: LightningReportScheduleStatus }) =>
			analyzeV2
				? updateAnalyzeReportSchedule(input, impersonation)
				: getClient(impersonation).request(updateLightningReportSchedule(), { input }),
		onSuccess: () => queryClient.invalidateQueries(lightningReportSchedulesQuery(impersonation, { enabled: true, analyzeV2 })),
		onError: (error) => console.error('Error updating lightning report schedule:', error),
	})
}

const GetFilteredReportDataUrlSchema = z.object({ getFilteredReportDataUrl: LightningFilteredReportDataUrlSchema })

export const useGetFilteredLightningReportUrl = () => {
	const analyzeV2 = useAnalyzeV2()

	return async (
		input: Omit<LightningGetFilteredReportDataUrlInput, 'fileType'> & { fileType: string },
		impersonation?: Impersonation,
	): Promise<string> => {
		if (analyzeV2) {
			const { reportId, fileType, filters, preferences } = input
			return createReportDownloadUrl(
				reportId,
				{ reportType: 'LIGHTNING', format: fileType, filters, preferences: { ...preferences, language: 'EN' } },
				impersonation,
			)
		}

		const response = await getClient(impersonation).request(getFilteredReportDataUrl, { input })
		return GetFilteredReportDataUrlSchema.parse(response).getFilteredReportDataUrl.url
	}
}

export const useGetLightningReportGeojson = (reportId: string | undefined, opts?: { disabled: boolean }) => {
	const impersonation = useImpersonation()
	const analyzeV2 = useAnalyzeV2()
	return useQuery({
		queryKey: ['LIGHTNING_REPORT_GEOJSON', reportId],
		staleTime: Infinity,
		queryFn:
			!reportId || opts?.disabled
				? skipToken
				: async () => {
						// analyze-v2 answers with the URLs themselves, which v1 wraps in `getReportDataUrls`
						const response = analyzeV2
							? { getReportDataUrls: await getAnalyzeReportData(reportId, impersonation) }
							: await getClient(impersonation).request(getLightningReportDataUrlsGQL(), {
									id: reportId,
								})
						const parsedUrls = GetReportFileUrlsSchema.parse(response)
						const geojson = await fetch(parsedUrls.getReportDataUrls.geojson).then((res) => res.json())
						const parsedGeojson = parseReportGeojson(geojson)
						return parsedGeojson
					},
	})
}

// Listed lightning report does not have geometry - however, let's keep it in the schema so that usage of `geometry`
// with places that have type `ListedLightningReport | LightningReport` is nicer.
const LightningReportFields = { assetVrns: AssetVrnsSchema, geometry: LightningReportSchema.shape.geometry }
export const ListedLightningReportSchema = z.preprocess(
	toAssetVrns,
	LightningReportSchema.omit({ assetVrn: true }).extend({ ...LightningReportFields, geometry: LightningReportFields.geometry.optional() }),
)

const GetLightningReportSchema = z.object({
	getReport: stripNulls(z.preprocess(toAssetVrns, LightningReportSchema.omit({ assetVrn: true }).extend(LightningReportFields))).nullable(),
})
const ListLightningReportsSchema = stripNulls(
	z.object({
		listReportsV2: z.object({
			items: z.array(ListedLightningReportSchema),
			nextToken: z.string().optional(),
		}),
	}),
)

export const ListedLightningReportScheduleSchema = z.preprocess(
	toAssetVrns,
	LightningReportScheduleBaseSchema.omit({ geometry: true, assetVrn: true })
		.extend({ assetVrns: AssetVrnsSchema })
		.and(z.union([RepeatedLightningReportScheduleFieldsSchema, OneOffLightningReportScheduleFieldsSchema])),
)
const ListLightningReportSchedulesSchema = stripNulls(
	z.object({
		listReportSchedules: z.object({
			items: z.array(ListedLightningReportScheduleSchema),
			nextToken: z.string().optional(),
		}),
	}),
)

// Only geojson is pre-generated and signed; downloads are built on demand from the
// applied filters via getFilteredReportDataUrl.
const GetReportFileUrlsSchema = stripNulls(z.object({ getReportDataUrls: z.object({ geojson: z.string() }) }))

const parseReportGeojson = (geojson: unknown) =>
	z
		.object({
			type: z.literal('FeatureCollection'),

			// Let's skip proper validation for features. Features array can have hundreds of thousands of elements
			// and validating that big of an array using zod is slow and causes UI to freeze.
			features: z.array(z.unknown()).transform((input) => {
				type InputElement = Feature<Point | GeometryCollection, { cloud: boolean; time: string; signalStrengthKA: number }>
				type OutputElement = Feature<
					Point | GeometryCollection,
					{
						cloud: boolean
						time: number
						signalStrengthKA: number
						distanceM?: number
						closestAssetVrns?: string[]
					} & (
						| {
								ellAngleDeg: number
								ellSemiMajM: number
								ellSemiMinM: number
						  }
						| {
								ellAngleDeg: undefined
								ellSemiMajM: undefined
								ellSemiMinM: undefined
						  }
					) &
						(
							| {
									isStrikePtStart: true
									continuingCurrentProb: LightningReportContinuingCurrentProbability
									metalDmgPotentialPctl: number // 0-100
									nonMetalDmgPotentialPctl: number // 0-100
									numStrokesInStrike?: number
							  }
							| {
									isStrikePtStart: false | undefined
									continuingCurrentProb: undefined
									metalDmgPotentialPctl: undefined
									nonMetalDmgPotentialPctl: undefined
									numStrokesInStrike?: undefined
							  }
						)
				>

				return (input as InputElement[]).map((typedInput) => {
					const typedOutput = typedInput as unknown as OutputElement
					// This transformation should ideally be done in the backend when report is being processed.
					typedOutput.properties.time = new Date(typedInput.properties.time).getTime()
					return typedOutput
				})
			}),
		})
		.parse(geojson)

export type ListedLightningReport = z.infer<typeof ListedLightningReportSchema>
export type ListedLightningReportSchedule = z.infer<typeof ListedLightningReportScheduleSchema>
export type RepeatedListedLightningReportSchedule = Extract<ListedLightningReportSchedule, { scheduleType: 'REPEATED' }>
export type OneOffListedLightningReportSchedule = Extract<ListedLightningReportSchedule, { scheduleType: 'ONE_OFF' }>

export type LightningReportGeojson = ReturnType<typeof parseReportGeojson>
export type LightningReportGeojsonFeature = LightningReportGeojson['features'][number]
export type LightningReportGeojsonFeatureProperties = LightningReportGeojsonFeature['properties']
export type LightningReportGeojsonFeatureField = keyof LightningReportGeojsonFeatureProperties
